import {
  type HubDeployment,
  HubDeploymentStatus,
} from '@robovision/hub'
import { i18n } from '~i18n'
import {
  type InferenceMetrics,
  InferenceStates,
} from './inferenceStatus'
import type { JobResources } from '../algorithm/jobDefinitions/common/JobResources'


export interface InferenceParams {
  active: boolean
  algorithmId: string
  algorithmVersion: string
  checksum: string
  createdAt: Date
  definitionId: string
  external: boolean
  id: string
  lastActivity?: Date
  logs: Record<string, unknown>[]
  maxInactivityPeriod?: number
  metrics: InferenceMetrics
  name: string
  projectId: string
  projectName: string
  replicas?: number
  resources: JobResources
  status: HubDeploymentStatus
  statusMessage?: string
  updatedAt: Date
  validDependencies: boolean
}

export class Inference {
  static readonly modelName = 'Inference'

  public readonly active: boolean
  public readonly algorithmId: string
  public readonly algorithmVersion: string
  public readonly checksum: string
  public readonly createdAt: Date
  public readonly definitionId: string
  public readonly external: boolean
  public readonly id: string
  public readonly lastActivity?: Date
  public readonly logs: Record<string, unknown>[]
  public readonly metrics: InferenceMetrics
  public readonly name: string
  public readonly projectId: string
  public readonly projectName: string
  public readonly maxInactivityPeriod?: number
  public readonly resources: JobResources
  public readonly replicas?: number
  public readonly status: HubDeploymentStatus
  public readonly statusMessage?: string
  public readonly updatedAt: Date
  public readonly validDependencies: boolean

  constructor (params: InferenceParams) {
    this.active = params.active
    this.algorithmId = params.algorithmId
    this.algorithmVersion = params.algorithmVersion
    this.checksum = params.checksum
    this.createdAt = params.createdAt
    this.definitionId = params.definitionId
    this.external = params.external
    this.id = params.id
    this.lastActivity = params.lastActivity
    this.logs = params.logs
    this.maxInactivityPeriod = params.maxInactivityPeriod
    this.metrics = params.metrics
    this.name = params.name
    this.projectId = params.projectId
    this.projectName = params.projectName
    this.resources = params.resources
    this.replicas = params.replicas
    this.status = params.status
    this.statusMessage = params.statusMessage
    this.updatedAt = params.updatedAt
    this.validDependencies = params.validDependencies
  }

  public static fromResponse (data: HubDeployment): Inference {
    const mainResources = data.runtimeParameters?.resources ?? {}

    return new Inference({
      active: data.active,
      algorithmId: data.algorithmId,
      algorithmVersion: data.algorithmVersion,
      checksum: data.checksum,
      createdAt: new Date(data.createdAt),
      definitionId: data.definitionId,
      external: data.external,
      id: data.id,
      lastActivity: data.metrics.lastActivity ? new Date(data.metrics.lastActivity) : undefined,
      logs: data.logs ?? [],
      maxInactivityPeriod: data.runtimeParameters.maxInactivityPeriod,
      metrics: data.metrics,
      name: data.name,
      projectId: data.projectId,
      projectName: data.projectName,
      replicas: data.runtimeParameters.replicas,
      resources: {
        main: {
          gpu: mainResources.limits?.gpu != null
            ? mainResources.limits?.gpu
            : undefined,
        },
      },
      status: data.status,
      statusMessage: data.statusMessage,
      updatedAt: new Date(data.updatedAt),
      validDependencies: data.validDependencies,
    })
  }

  public get isHealthy (): boolean {
    return this.state === InferenceStates.Healthy
  }

  public get isIncomplete (): boolean {
    return this.state === InferenceStates.Stopped && !this.validDependencies
  }

  public get isFailed (): boolean {
    return this.state === InferenceStates.Failed
  }

  public get isStopping (): boolean {
    return this.state === InferenceStates.Stopping
  }

  public get isTerminated (): boolean {
    return this.state === InferenceStates.Failed ||
      this.state === InferenceStates.Stopped
  }

  public get isUnresponsive (): boolean {
    return this.state === InferenceStates.Unresponsive
  }

  public get isInProgress (): boolean {
    return this.active && [
      HubDeploymentStatus.QUEUED,
      HubDeploymentStatus.STARTING,
      HubDeploymentStatus.FAILED,
    ].includes(this.status)
  }

  public get description (): string {
    switch (this.state) {
      case InferenceStates.Failed:
        return i18n.t('inference.status.failed.description')

      case InferenceStates.Healthy:
        return i18n.t('inference.status.healthy.description')

      case InferenceStates.Preparing:
        return i18n.t('inference.status.preparing.description')

      case InferenceStates.Stopped:
        return i18n.t('inference.status.stopped.description')

      case InferenceStates.Stopping:
        return i18n.t('inference.status.stopping.description')

      case InferenceStates.Unresponsive:
        return i18n.t('inference.status.unresponsive.description')

      default:
        return i18n.t('inference.status.unknown.description')
    }
  }

  public get label (): string {
    switch (this.state) {
      case InferenceStates.Failed:
        return i18n.t('inference.status.failed.label')

      case InferenceStates.Healthy:
        return i18n.t('inference.status.healthy.label')

      case InferenceStates.Preparing:
        return i18n.t('inference.status.preparing.label')

      case InferenceStates.Stopped:
        return i18n.t('inference.status.stopped.label')

      case InferenceStates.Stopping:
        return i18n.t('inference.status.stopping.label')

      case InferenceStates.Unresponsive:
        return i18n.t('inference.status.unresponsive.label')

      default:
        return i18n.t('inference.status.unknown.label')
    }
  }

  public get startActionLabel (): string {
    switch (this.state) {
      case InferenceStates.Failed:
      case InferenceStates.Stopped:
        return i18n.t('inference.actions.restart')

      default:
        return ''
    }
  }

  public get state (): InferenceStates {
    if (this.active) {
      if (this.metrics.unresponsive) {
        return InferenceStates.Unresponsive
      }
    }

    switch (this.status) {
      case HubDeploymentStatus.FAILED:
        return InferenceStates.Failed

      case HubDeploymentStatus.RUNNING:
        // An algorithm can (optionally) tell the platform if it’s ready via the `ready` flag:
        // - `null` means the algorithm doesn't support this feature.
        // - `false` means the algorithm is preparing for inference.
        // - `true` means the algorithm is ready to process inference requests.
        if (this.metrics.ready === false) {
          return InferenceStates.Preparing
        }
        return InferenceStates.Healthy

      case HubDeploymentStatus.STARTING:
      case HubDeploymentStatus.QUEUED:
        return InferenceStates.Preparing

      case HubDeploymentStatus.STOPPED:
        return InferenceStates.Stopped

      case HubDeploymentStatus.STOPPING:
        return InferenceStates.Stopping

      default:
        return InferenceStates.Unknown
    }
  }
}
